import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, takeUntil, switchMap } from 'rxjs/operators';
import { format } from 'date-fns';
import * as SharedAnalysisResultsActions from '../actions/shared-analysis-results.actions';
import { SubscriptionService } from '../../common-store/services/subscription.service';
import { SharedAnalysisResultsService } from '../services/shared-analysis-results.service';
import {
  ErrorStore,
  ErrorStoreStatusEnum,
  Recording,
  Horse,
  Event,
  DbStatus,
  SharedAnalysisResultsResponse,
} from '@sleipai/sleip-models';
import { of } from 'rxjs';
import * as AnalysisActions from '../../analysis-store/actions/analysis.actions';
import * as HorsesActions from '../../horses-store/actions/horses.actions';
import * as RecordingActions from '../../recordings-store/actions/recording.actions';
import { v4 as uuidv4 } from 'uuid';
import { EventsActions } from '../../events-store';
import * as AuthActions from '../../auth-store/actions/auth.actions';

@Injectable()
export class SharedAnalysisResultsEffects {
  loadSharedAnalysisResults$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        SharedAnalysisResultsActions.loadSharedAnalysisResultsByIdAndPublicKey
      ),
      switchMap(({ publicRecordingId, publicKey, version }) => {
        return this.sharedAnalysisResultsService
          .loadSharedAnalysisResultsByIdAndKey(
            publicRecordingId,
            publicKey,
            version
          )
          .pipe(
            takeUntil(this.subService.unsubscribe$),
            switchMap((data: SharedAnalysisResultsResponse) => {
              const sharedResults = data.detail;
              const horse = sharedResults[0]?.horse;
              const userAppAccessLevel = data.userType;

              const recordings = sharedResults.map((sharedResultItem) => {
                return {
                  ...sharedResultItem.recording,
                  dbStatus: DbStatus.ACTIVE,
                  horseId: horse.id ?? horse.horseId,
                };
              });
              const events = this.mockupEventsForSharedRecordings(
                recordings,
                horse
              );
              return [
                SharedAnalysisResultsActions.loadSharedAnalysisResultsListSuccess(
                  {
                    data: sharedResults,
                  }
                ),
                AnalysisActions.loadMultipleAnalysisResultsByIdsSuccess({
                  analysisResults: sharedResults.map(
                    (sharedItem) => sharedItem.analysisResults
                  ),
                }),
                RecordingActions.addRecordings({ recordings, hasMore: false }),
                HorsesActions.addSingleHorse({ horse }),
                EventsActions.loadEventsListForHorseSuccess({ events }),
                AuthActions.notAuthenticated(),
                AuthActions.updateAuthUserAccessLevel({
                  userAppAccessLevel,
                }),
              ];
            })
          );
      }),
      catchError(({ message, code }) => {
        const firebaseErrorCode = code as string;
        const error: ErrorStore = {
          message: message,
          status:
            firebaseErrorCode?.replace('functions/', '') ??
            ErrorStoreStatusEnum.INTERNAL,
        };
        return of(
          SharedAnalysisResultsActions.loadSharedAnalysisResultsFailure({
            error,
          })
        );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private sharedAnalysisResultsService: SharedAnalysisResultsService,
    private subService: SubscriptionService
  ) {}

  private mockupEventsForSharedRecordings(
    sharedRecordings: Partial<Recording>[],
    horse: Partial<Horse>
  ) {
    const eventList = Array.from<Event>([]);
    const horseId = horse.id ?? horse.horseId;
    if (!horseId) return eventList;

    const groupedByDate = sharedRecordings.reduce((result, item) => {
      if (!item.creationDate) return result;
      const key = format(item.creationDate, 'yyyy-MM-dd');

      if (!result.has(key)) {
        result.set(key, []);
      }

      const groupdDateRecordings = result.get(key) ?? [];

      result.set(key, [...groupdDateRecordings, item]);

      return result;
    }, new Map<string, Partial<Recording>[]>());

    groupedByDate.forEach((recordings, key) => {
      const event: Event = {
        id: uuidv4(),
        date: new Date(key),
        recordings: recordings
          .map((recording) => recording.recordingId)
          .filter((id) => id !== undefined),
        dbStatus: DbStatus.ACTIVE,
        horseId: horseId,
      };
      eventList.push(event);
    });
    return eventList;
  }
}
